import { useAuth } from "@clerk/clerk-react";

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    // Read from runtime config (production) or build-time env (local dev)
    const runtimeBackendUrl = window.__CONFIG__?.VITE_BACKEND_URL;
    this.baseUrl =
      baseUrl ||
      runtimeBackendUrl ||
      import.meta.env.VITE_BACKEND_URL ||
      "http://localhost:8010";
  }

  private async getAuthHeaders(
    token?: string,
    includeContentType = true
  ): Promise<HeadersInit> {
    const headers: HeadersInit = {};

    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit & { token?: string; isFormData?: boolean } = {}
  ): Promise<T> {
    const { token, isFormData, ...fetchOptions } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const headers = await this.getAuthHeaders(token, !isFormData);

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...headers,
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`
      );
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }

    return response as unknown as T;
  }

  async get<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET", token });
  }

  async post<T>(endpoint: string, data?: unknown, token?: string): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(endpoint, {
      method: "POST",
      body: isFormData ? data : JSON.stringify(data),
      token,
      isFormData,
    });
  }

  async put<T>(endpoint: string, data?: unknown, token?: string): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(endpoint, {
      method: "PUT",
      body: isFormData ? data : JSON.stringify(data),
      token,
      isFormData,
    });
  }

  async delete<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE", token });
  }

  async postForm<T>(
    endpoint: string,
    formData: FormData,
    token?: string
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: formData,
      token,
      isFormData: true,
    });
  }

  async postRaw(
    endpoint: string,
    data?: unknown,
    token?: string
  ): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    const authHeaders = await this.getAuthHeaders(token, true);

    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers: authHeaders,
    });

    return response;
  }
}

export const useApiClient = () => {
  const { getToken } = useAuth();
  const apiClient = new ApiClient();

  const authenticatedRequest = async <T>(
    requestFn: (token: string) => Promise<T>
  ): Promise<T> => {
    const token = await getToken();
    if (!token) {
      throw new Error("No authentication token available");
    }
    return requestFn(token);
  };

  /**
   * Forces a token refresh and returns a new token.
   * Useful for retrying requests after authentication failures.
   */
  const refreshToken = async (): Promise<string> => {
    const token = await getToken({ skipCache: true });
    if (!token) {
      throw new Error("Failed to refresh authentication token");
    }
    return token;
  };

  return {
    apiClient,
    authenticatedRequest,
    getToken,
    refreshToken,
  };
};
